import React, { type KeyboardEvent, type ReactNode, useEffect, useRef } from "react";

import { MenuItem, type MenuItemOptions } from "primereact/menuitem";
import { Menubar } from "primereact/menubar";

import { NavLogo } from "./NavLogo/NavLogo";
import type { MobileNavEntry } from "./MobileNav/MobileNavDrawer";

import S from "./UserNavBar.module.scss";

interface DesktopNavBarProps {
  navbarRef?: React.RefObject<HTMLDivElement>;
  testId: string;
  thumbLogoUrl?: string;
  navigateTo: (route: string, openInNewTab?: boolean) => void;
  navEntries: MobileNavEntry[];
  end: ReactNode;
  dialogTemplate?: (props: {
    showDialog: boolean;
    setShowDialog: (value: boolean) => void;
    onConfirm: () => void;
  }) => ReactNode;
  showDialog: boolean;
  setShowDialog: (value: boolean) => void;
  confirmNavigation: () => void;
}

/**
 * Nav used at/above the `xl` breakpoint: PrimeReact Menubar with the full item
 * list, including the Champion Passport dropdown. Below `xl`, UserNavBar renders
 * MobileNavBar instead.
 */
export function DesktopNavBar({
  navbarRef,
  testId,
  thumbLogoUrl,
  navigateTo,
  navEntries,
  end,
  dialogTemplate,
  showDialog,
  setShowDialog,
  confirmNavigation,
}: DesktopNavBarProps) {
  // WCAG fix: PrimeReact Menubar renders <ul role="menu"> for every item's submenu,
  // even when there are no children. Empty role="menu" elements are flagged as broken ARIA menus.
  const menubarNavRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const nav = menubarNavRef.current;
    if (!nav) return;
    nav.querySelectorAll<HTMLUListElement>('ul.p-submenu-list[role="menu"]').forEach(ul => {
      if (ul.children.length === 0) {
        ul.removeAttribute("role");
      }
    });
  });

  // WCAG 2.2 AA: PrimeReact Menubar sets tabindex="-1" on all <a> tags,
  // so we use custom templates with tabIndex={0} to make items tabbable.

  // Template for simple nav links (no submenu)
  function NavMenuLink(
    item: MenuItem,
    options: MenuItemOptions,
    route: string,
    openInNewTab?: boolean
  ) {
    return (
      <a
        className={options.className}
        tabIndex={0}
        onClick={e => {
          e.preventDefault();
          navigateTo(route, openInNewTab);
        }}
        onKeyDown={(e: KeyboardEvent<HTMLAnchorElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigateTo(route, openInNewTab);
          }
        }}
      >
        <span className={options.labelClassName}>{item.label}</span>
      </a>
    );
  }

  // Template for dropdown parent items (has submenu children)
  function NavMenuDropdown(item: MenuItem, options: MenuItemOptions) {
    return (
      <a
        className={options.className}
        tabIndex={0}
        onClick={options.onClick}
        onKeyDown={(e: KeyboardEvent<HTMLAnchorElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            options.onClick(e as unknown as React.SyntheticEvent);
          }
        }}
      >
        <span className={options.labelClassName}>{item.label}</span>
        <i className="pi pi-angle-down p-submenu-icon" />
      </a>
    );
  }

  const items: MenuItem[] = navEntries.map(entry => ({
    label: entry.label,
    visible: entry.visible,
    ...("children" in entry
      ? {
          template: (item: MenuItem, options: MenuItemOptions) => NavMenuDropdown(item, options),
          items: entry.children.map(child => ({
            label: child.label,
            visible: child.visible,
            template: (item: MenuItem, options: MenuItemOptions) =>
              NavMenuLink(item, options, child.route, child.openInNewTab),
          })),
        }
      : {
          template: (item: MenuItem, options: MenuItemOptions) =>
            NavMenuLink(item, options, entry.route, entry.openInNewTab),
        }),
  }));

  return (
    <nav
      ref={(node: HTMLElement | null) => {
        menubarNavRef.current = node;
        if (navbarRef)
          (navbarRef as React.MutableRefObject<HTMLDivElement | null>).current =
            node as HTMLDivElement | null;
      }}
      aria-label="Main navigation"
      data-testid={`${testId}-menubar`}
    >
      <Menubar
        start={<NavLogo navigateTo={navigateTo} thumbLogoUrl={thumbLogoUrl} />}
        model={items}
        end={end}
        className={S.menubar}
        pt={{
          menu: { className: "gap-1" },
          content: { className: "body-sm" },
          action: { className: "px-3" },
          button: { className: S.menubarButton },
          icon: { className: S.menubarIcon },
          end: { className: S.menubarEnd },
        }}
      />
      {dialogTemplate?.({
        showDialog,
        setShowDialog,
        onConfirm: confirmNavigation,
      })}
    </nav>
  );
}
